import { useState, useEffect, useMemo } from 'react';
import { getSyncEvents, getFirmwareVersions, type FirmwareVersion } from '../api';
import type { SyncEvent, EventType } from '../types';
import { QUARANTINE_REASON_LABELS } from '../types';
import { getEventTypeLabel, getEventTypeIcon, formatRelativeTime } from '../utils';
import styles from './Analytics.module.css';

/**
 * Analytics Page
 * 
 * Comprehensive visualizations for sync system health monitoring.
 * Computes all analytics directly from events data.
 */

interface DailyStats {
  date: string;
  total: number;
  corrupted: number;
  quarantined: number;
  errors: number;
  uploads: number;
  downloads: number;
  // Corruption by event type
  uploadTotal: number;
  uploadCorrupted: number;
  downloadTotal: number;
  downloadCorrupted: number;
  handshakeTotal: number;
  handshakeCorrupted: number;
  // Deduplication and conflicts
  deduplicationEvents: number;
  deduplicatedFiles: number;
  massDeduplication: number;
  conflictsCreated: number;
  conflictsResolved: number;
}

interface DeviceHealth {
  device_code: string;
  device_name: string | null;
  user_email: string | null;
  total_events: number;
  uploads: { total: number; corrupted: number };
  downloads: { total: number; corrupted: number };
  handshakes: { total: number; corrupted: number };
  last_sync: string;
}

interface UserActivity {
  user_email: string;
  device_count: number;
  total_events: number;
  corruption_count: number;
  last_sync: string;
}

export default function Analytics() {
  const [events, setEvents] = useState<SyncEvent[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [firmwareVersions, setFirmwareVersions] = useState<FirmwareVersion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d' | 'all'>('24h');
  const [corruptionTrendRange, setCorruptionTrendRange] = useState<'24h' | '7d' | '30d'>('7d');
  const [firmwareChartRange, setFirmwareChartRange] = useState<'24h' | '7d' | '30d'>('7d');

  useEffect(() => {
    async function fetchAllEvents() {
      setIsLoading(true);
      setError(null);
      setEvents([]); // Clear previous data
      setTotalCount(0);
      setLoadingProgress('Fetching events...');

      try {
        const days = timeRange === '24h' ? 1 : timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 365;
        const since = new Date();
        if (timeRange === '24h') {
          since.setHours(since.getHours() - 24);
        } else {
        since.setDate(since.getDate() - days);
        }

        const baseFilters = {
          timestamp_gte: timeRange !== 'all' ? since.toISOString() : undefined,
          ordering: '-timestamp',
        };

        console.log(`[Analytics] Fetching events for ${timeRange} (since ${since.toISOString()})`);

        // Fetch first page to get total count
        const firstPage = await getSyncEvents({
          ...baseFilters,
          page_size: 200,
          page: 1,
        });

        const total = firstPage.count || 0;
        setTotalCount(total);
        
        let allEvents = [...(firstPage.results || [])];
        setLoadingProgress(`Loading ${allEvents.length} of ${total} events...`);

        // Fetch remaining pages if needed (limit to reasonable amount for analytics)
        const maxEvents = 5000; // Cap for performance
        const pageSize = 200;
        const maxPages = Math.min(Math.ceil(total / pageSize), Math.ceil(maxEvents / pageSize));
        
        for (let page = 2; page <= maxPages; page++) {
          if (allEvents.length >= maxEvents) break;
          
          const pageData = await getSyncEvents({
            ...baseFilters,
            page_size: pageSize,
            page,
          });
          
          allEvents = [...allEvents, ...(pageData.results || [])];
          setLoadingProgress(`Loading ${allEvents.length} of ${Math.min(total, maxEvents)} events...`);
        }

        console.log(`[Analytics] Loaded ${allEvents.length} of ${total} total events`);
        setEvents(allEvents);
      } catch (err) {
        console.error('[Analytics] Error:', err);
        setError('Failed to load analytics data');
      } finally {
        setIsLoading(false);
        setLoadingProgress('');
      }
    }

    fetchAllEvents();
  }, [timeRange]);

  // Fetch firmware versions (separate from events, doesn't depend on timeRange)
  useEffect(() => {
    async function fetchFirmware() {
      try {
        const versions = await getFirmwareVersions();
        setFirmwareVersions(versions);
        console.log('[Analytics] Firmware versions loaded:', versions.length);
      } catch (err) {
        console.warn('[Analytics] Failed to load firmware versions:', err);
        // Don't set error - firmware is optional data
      }
    }
    fetchFirmware();
  }, []);

  // Compute all analytics from events
  const analytics = useMemo(() => {
    if (events.length === 0) return null;

    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const days = timeRange === '24h' ? 1 : timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;

    // Daily stats
        const dailyMap = new Map<string, DailyStats>();
        for (let i = 0; i < days; i++) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          const dateStr = date.toISOString().split('T')[0];
          dailyMap.set(dateStr, {
            date: dateStr,
            total: 0,
            corrupted: 0,
            quarantined: 0,
            errors: 0,
        uploads: 0,
        downloads: 0,
        uploadTotal: 0,
        uploadCorrupted: 0,
        downloadTotal: 0,
        downloadCorrupted: 0,
        handshakeTotal: 0,
        handshakeCorrupted: 0,
        deduplicationEvents: 0,
        deduplicatedFiles: 0,
        massDeduplication: 0,
        conflictsCreated: 0,
        conflictsResolved: 0,
      });
    }

    // Aggregations
    const eventsByType: Record<string, number> = {};
    const corruptionByType: Record<string, { total: number; corrupted: number }> = {
      sync_handshake: { total: 0, corrupted: 0 },
      file_upload: { total: 0, corrupted: 0 },
      file_download: { total: 0, corrupted: 0 },
    };
    const corruptionCategories: Record<string, number> = {};
    const quarantineReasons: Record<string, number> = {};
    const deviceMap = new Map<string, DeviceHealth>();
    const userMap = new Map<string, UserActivity>();
    const firmwareVersions: Record<string, { 
      total: number;
      uploads: { total: number; corrupted: number };
      downloads: { total: number; corrupted: number };
      handshakes: { total: number; corrupted: number };
    }> = {};
    const hourlyDistribution: number[] = new Array(24).fill(0);
    
    let totalCorrupted = 0;
    let totalQuarantined = 0;
    let totalErrors = 0;
    let eventsLast24h = 0;
    let corruptedLast24h = 0;
    let totalDeduplicationEvents = 0;
    let totalDeduplicatedFiles = 0;
    let totalMassDeduplication = 0;
    let totalConflictsCreated = 0;
    let totalConflictsResolved = 0;
    
    const uniqueUsers = new Set<string>();
    const uniqueDevices = new Set<string>();
    const uniqueSessions = new Set<string>();

        for (const event of events) {
          const dateStr = event.timestamp.split('T')[0];
          const day = dailyMap.get(dateStr);
      const eventDate = new Date(event.timestamp);
      const hour = eventDate.getHours();

      // Empty uploads are benign (user sent a 0-byte file) — exclude them
      // from corruption/quarantine/health metrics so they don't drag down scores.
      const isEmptyUpload = event.quarantine_reason === 'EMPTY_UPLOAD';
      
      // Hourly distribution
      hourlyDistribution[hour]++;
          
          if (day) {
            day.total++;
            if (event.has_suspicious_chars && !isEmptyUpload) day.corrupted++;
            if (event.was_quarantined && !isEmptyUpload) day.quarantined++;
            if (!event.success) day.errors++;
        if (event.event_type === 'file_upload') {
          day.uploads++;
          day.uploadTotal++;
          if (event.has_suspicious_chars && !isEmptyUpload) day.uploadCorrupted++;
        }
        if (event.event_type === 'file_download') {
          day.downloads++;
          day.downloadTotal++;
          if (event.has_suspicious_chars) day.downloadCorrupted++;
        }
        if (event.event_type === 'sync_handshake') {
          day.handshakeTotal++;
          if (event.has_suspicious_chars) day.handshakeCorrupted++;
        }
        // Deduplication tracking
        if (event.has_name_deduplication) {
          day.deduplicationEvents++;
          day.deduplicatedFiles += event.name_deduplication_count || 0;
          if (event.is_mass_deduplication) day.massDeduplication++;
        }
        // Conflict tracking
        if (event.event_type === 'conflict_created') {
          day.conflictsCreated++;
        }
        if (event.event_type === 'conflict_resolved') {
          day.conflictsResolved++;
        }
      }

      // Event types
      eventsByType[event.event_type] = (eventsByType[event.event_type] || 0) + 1;

      // Track corruption by event type (only for the 3 main types)
      const eventType = event.event_type;
      if (eventType in corruptionByType) {
        corruptionByType[eventType].total++;
        if (event.has_suspicious_chars && !isEmptyUpload) {
          corruptionByType[eventType].corrupted++;
        }
      }

      // Corruption categories
      if (event.has_suspicious_chars && !isEmptyUpload) {
        totalCorrupted++;
        if (event.suspicious_details) {
            const categories = extractCategories(event.suspicious_details);
            for (const cat of categories) {
            corruptionCategories[cat] = (corruptionCategories[cat] || 0) + 1;
          }
        }
      }

      // Quarantine reasons (still tracked for display, but empty uploads
      // are excluded from the totalQuarantined count used in health score)
      if (event.was_quarantined) {
        if (!isEmptyUpload) totalQuarantined++;
        const reason = event.quarantine_reason || 'UNKNOWN';
        quarantineReasons[reason] = (quarantineReasons[reason] || 0) + 1;
      }

      // Errors
      if (!event.success) totalErrors++;

      // Deduplication totals
      if (event.has_name_deduplication) {
        totalDeduplicationEvents++;
        totalDeduplicatedFiles += event.name_deduplication_count || 0;
        if (event.is_mass_deduplication) totalMassDeduplication++;
      }
      
      // Conflict totals
      if (event.event_type === 'conflict_created') totalConflictsCreated++;
      if (event.event_type === 'conflict_resolved') totalConflictsResolved++;

      // 24h stats
      if (eventDate >= yesterday) {
        eventsLast24h++;
        if (event.has_suspicious_chars && !isEmptyUpload) corruptedLast24h++;
      }

      // Unique tracking
      if (event.user_email) uniqueUsers.add(event.user_email);
      if (event.device_code) uniqueDevices.add(event.device_code);
      if (event.session_id) uniqueSessions.add(event.session_id);

      // Device health - track corruption by event type
      if (event.device_code) {
        const device = deviceMap.get(event.device_code) || {
          device_code: event.device_code,
          device_name: event.device_name,
          user_email: event.user_email,
          total_events: 0,
          uploads: { total: 0, corrupted: 0 },
          downloads: { total: 0, corrupted: 0 },
          handshakes: { total: 0, corrupted: 0 },
          last_sync: event.timestamp,
        };
        device.total_events++;
        
        if (event.event_type === 'file_upload') {
          device.uploads.total++;
          if (event.has_suspicious_chars && !isEmptyUpload) device.uploads.corrupted++;
        } else if (event.event_type === 'file_download') {
          device.downloads.total++;
          if (event.has_suspicious_chars) device.downloads.corrupted++;
        } else if (event.event_type === 'sync_handshake') {
          device.handshakes.total++;
          if (event.has_suspicious_chars) device.handshakes.corrupted++;
        }
        
        if (event.timestamp > device.last_sync) device.last_sync = event.timestamp;
        deviceMap.set(event.device_code, device);
      }

      // User activity
      if (event.user_email) {
        const user = userMap.get(event.user_email) || {
          user_email: event.user_email,
          device_count: 0,
          total_events: 0,
          corruption_count: 0,
          last_sync: event.timestamp,
        };
        user.total_events++;
        if (event.has_suspicious_chars && !isEmptyUpload) user.corruption_count++;
        if (event.timestamp > user.last_sync) user.last_sync = event.timestamp;
        userMap.set(event.user_email, user);
      }

      // Firmware versions - track corruption by event type
      if (event.firmware_version) {
        const fw = firmwareVersions[event.firmware_version] || { 
          total: 0,
          uploads: { total: 0, corrupted: 0 },
          downloads: { total: 0, corrupted: 0 },
          handshakes: { total: 0, corrupted: 0 },
        };
        fw.total++;
        
        if (event.event_type === 'file_upload') {
          fw.uploads.total++;
          if (event.has_suspicious_chars && !isEmptyUpload) fw.uploads.corrupted++;
        } else if (event.event_type === 'file_download') {
          fw.downloads.total++;
          if (event.has_suspicious_chars) fw.downloads.corrupted++;
        } else if (event.event_type === 'sync_handshake') {
          fw.handshakes.total++;
          if (event.has_suspicious_chars) fw.handshakes.corrupted++;
        }
        
        firmwareVersions[event.firmware_version] = fw;
      }
    }

    // Calculate device counts per user
    const devicesByUser = new Map<string, Set<string>>();
    for (const event of events) {
      if (event.user_email && event.device_code) {
        const devices = devicesByUser.get(event.user_email) || new Set();
        devices.add(event.device_code);
        devicesByUser.set(event.user_email, devices);
      }
    }
    for (const [email, devices] of devicesByUser) {
      const user = userMap.get(email);
      if (user) user.device_count = devices.size;
    }

    const dailyStats = Array.from(dailyMap.values())
          .sort((a, b) => a.date.localeCompare(b.date));

    const devices = Array.from(deviceMap.values())
      .sort((a, b) => b.total_events - a.total_events);

    const users = Array.from(userMap.values())
      .sort((a, b) => b.total_events - a.total_events);

    // Calculate corruption rates by type
    const uploadCorruptionRate = corruptionByType.file_upload.total > 0 
      ? corruptionByType.file_upload.corrupted / corruptionByType.file_upload.total : 0;
    const downloadCorruptionRate = corruptionByType.file_download.total > 0 
      ? corruptionByType.file_download.corrupted / corruptionByType.file_download.total : 0;
    const handshakeCorruptionRate = corruptionByType.sync_handshake.total > 0 
      ? corruptionByType.sync_handshake.corrupted / corruptionByType.sync_handshake.total : 0;

    // Health score calculation - weight by upload corruption (most critical)
    const quarantineRate = events.length > 0 ? totalQuarantined / events.length : 0;
    const errorRate = events.length > 0 ? totalErrors / events.length : 0;
    const healthScore = Math.max(0, Math.min(100, 
      100 - (uploadCorruptionRate * 100 * 5) - (downloadCorruptionRate * 100 * 3) - (quarantineRate * 100 * 5) - (errorRate * 100 * 2)
    ));

    return {
      totalEvents: events.length,
      totalCorrupted,
      totalQuarantined,
      totalErrors,
      corruptionByType,
      uploadCorruptionRate,
      downloadCorruptionRate,
      handshakeCorruptionRate,
      quarantineRate,
      errorRate,
      healthScore,
      eventsLast24h,
      corruptedLast24h,
      uniqueUsers: uniqueUsers.size,
      uniqueDevices: uniqueDevices.size,
      uniqueSessions: uniqueSessions.size,
      dailyStats,
      eventsByType,
      corruptionCategories,
      quarantineReasons,
      devices,
      users,
      firmwareVersions,
      hourlyDistribution,
      // Deduplication & Conflicts
      totalDeduplicationEvents,
      totalDeduplicatedFiles,
      totalMassDeduplication,
      totalConflictsCreated,
      totalConflictsResolved,
    };
  }, [events, timeRange]);

  // Compute firmware distribution over time (for handshakes only)
  const firmwareTimeline = useMemo(() => {
    if (events.length === 0) return { days: [], versions: [] };

    const now = new Date();
    const rangeHours = firmwareChartRange === '24h' ? 24 : firmwareChartRange === '7d' ? 168 : 720;
    const since = new Date(now.getTime() - rangeHours * 60 * 60 * 1000);

    // Filter to handshake events within range
    const handshakes = events.filter(e => 
      e.event_type === 'sync_handshake' && 
      new Date(e.timestamp) >= since &&
      e.firmware_version
    );

    // Group by day and firmware version
    const dailyFirmware: Record<string, Record<string, number>> = {};
    const allVersions = new Set<string>();

    for (const event of handshakes) {
      const dateStr = event.timestamp.split('T')[0];
      const version = event.firmware_version!;
      
      if (!dailyFirmware[dateStr]) {
        dailyFirmware[dateStr] = {};
      }
      dailyFirmware[dateStr][version] = (dailyFirmware[dateStr][version] || 0) + 1;
      allVersions.add(version);
    }

    // Sort dates and versions
    const sortedDates = Object.keys(dailyFirmware).sort();
    const sortedVersions = Array.from(allVersions).sort((a, b) => {
      // Sort versions semantically if possible
      const aParts = a.split('.').map(Number);
      const bParts = b.split('.').map(Number);
      for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
        const aNum = aParts[i] || 0;
        const bNum = bParts[i] || 0;
        if (aNum !== bNum) return bNum - aNum; // Descending (newest first)
      }
      return 0;
    });

    return {
      days: sortedDates.map(date => ({
        date,
        label: new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        versions: sortedVersions.map(v => ({
          version: v,
          count: dailyFirmware[date][v] || 0,
        })),
        total: sortedVersions.reduce((sum, v) => sum + (dailyFirmware[date][v] || 0), 0),
      })),
      versions: sortedVersions,
      totalHandshakes: handshakes.length,
    };
  }, [events, firmwareChartRange]);

  // Color palette for firmware versions
  function getFirmwareColor(index: number): string {
    const colors = [
      '#22c55e', // green - newest/most common
      '#3b82f6', // blue
      '#f59e0b', // amber
      '#8b5cf6', // purple
      '#ec4899', // pink
      '#06b6d4', // cyan
      '#f97316', // orange
      '#6366f1', // indigo
    ];
    return colors[index % colors.length];
  }

  function extractCategories(details: string): string[] {
    const categories: string[] = [];
    const knownCategories = [
      'NULL_BYTES', 'CONTROL_CHARS', 'TLS_CERT_DATA', 'ENCODING_GARBAGE',
      'REPEATED_NULL_DISPLAY', 'REPEATED_AT', 'REPEATED_PERCENT',
      'REPEATED_QUESTION', 'MIXED_GARBAGE'
    ];
    for (const cat of knownCategories) {
      if (details.includes(cat)) {
        categories.push(cat);
      }
    }
    return categories.length > 0 ? categories : ['UNKNOWN'];
  }

  // Compute corruption trend data based on selected range
  const corruptionTrendData = useMemo(() => {
    if (!analytics) return [];
    
    const now = new Date();
    let daysToShow: number;
    let groupBy: 'hour' | 'day';
    
    if (corruptionTrendRange === '24h') {
      daysToShow = 1;
      groupBy = 'hour';
    } else if (corruptionTrendRange === '7d') {
      daysToShow = 7;
      groupBy = 'day';
    } else {
      daysToShow = 30;
      groupBy = 'day';
    }
    
    if (groupBy === 'hour') {
      // Group by hour for last 24 hours
      const hourlyData: Array<{
        label: string;
        uploadRate: number;
        downloadRate: number;
        handshakeRate: number;
        uploadTotal: number;
        downloadTotal: number;
        handshakeTotal: number;
      }> = [];
      
      for (let i = 23; i >= 0; i--) {
        const hourStart = new Date(now.getTime() - i * 60 * 60 * 1000);
        const hourEnd = new Date(hourStart.getTime() + 60 * 60 * 1000);
        
        let uploadTotal = 0, uploadCorrupted = 0;
        let downloadTotal = 0, downloadCorrupted = 0;
        let handshakeTotal = 0, handshakeCorrupted = 0;
        
        for (const event of events) {
          const eventDate = new Date(event.timestamp);
          if (eventDate >= hourStart && eventDate < hourEnd) {
            if (event.event_type === 'file_upload') {
              uploadTotal++;
              if (event.has_suspicious_chars) uploadCorrupted++;
            } else if (event.event_type === 'file_download') {
              downloadTotal++;
              if (event.has_suspicious_chars) downloadCorrupted++;
            } else if (event.event_type === 'sync_handshake') {
              handshakeTotal++;
              if (event.has_suspicious_chars) handshakeCorrupted++;
            }
          }
        }
        
        hourlyData.push({
          label: `${hourStart.getHours()}:00`,
          uploadRate: uploadTotal > 0 ? (uploadCorrupted / uploadTotal) * 100 : 0,
          downloadRate: downloadTotal > 0 ? (downloadCorrupted / downloadTotal) * 100 : 0,
          handshakeRate: handshakeTotal > 0 ? (handshakeCorrupted / handshakeTotal) * 100 : 0,
          uploadTotal,
          downloadTotal,
          handshakeTotal,
        });
      }
      
      return hourlyData;
    } else {
      // Group by day
      const cutoffDate = new Date(now.getTime() - daysToShow * 24 * 60 * 60 * 1000);
      
      return analytics.dailyStats
        .filter(day => new Date(day.date) >= cutoffDate)
        .map(day => ({
          label: day.date.slice(5), // MM-DD
          uploadRate: day.uploadTotal > 0 ? (day.uploadCorrupted / day.uploadTotal) * 100 : 0,
          downloadRate: day.downloadTotal > 0 ? (day.downloadCorrupted / day.downloadTotal) * 100 : 0,
          handshakeRate: day.handshakeTotal > 0 ? (day.handshakeCorrupted / day.handshakeTotal) * 100 : 0,
          uploadTotal: day.uploadTotal,
          downloadTotal: day.downloadTotal,
          handshakeTotal: day.handshakeTotal,
        }));
    }
  }, [analytics, events, corruptionTrendRange]);

  const getHealthColor = (score: number) => {
    if (score >= 90) return '#22c55e';
    if (score >= 70) return '#eab308';
    if (score >= 50) return '#f97316';
    return '#ef4444';
  };

  const getHealthLabel = (score: number) => {
    if (score >= 90) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 50) return 'Fair';
    return 'Poor';
  };

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}>📊</div>
          <div>{loadingProgress || 'Loading analytics...'}</div>
        </div>
      </div>
    );
  }

  if (!analytics || events.length === 0) {
  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Analytics</h1>
          <p className={styles.subtitle}>Sync system health and trends</p>
          </div>
        </div>
        <div className={styles.empty}>
          <p>No events found for the selected time range.</p>
        </div>
      </div>
    );
  }

  const maxDailyTotal = Math.max(...analytics.dailyStats.map(d => d.total), 1);
  const maxHourly = Math.max(...analytics.hourlyDistribution, 1);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Analytics</h1>
          <p className={styles.subtitle}>
            Analyzing {analytics.totalEvents.toLocaleString()} events from {analytics.uniqueUsers} users
            {totalCount > analytics.totalEvents && (
              <span className={styles.totalNote}> (of {totalCount.toLocaleString()} total)</span>
            )}
          </p>
        </div>
        <div className={styles.timeToggle}>
          <button 
            className={`${styles.toggleButton} ${timeRange === '24h' ? styles.active : ''}`}
            onClick={() => setTimeRange('24h')}
          >
            24 Hours
          </button>
          <button 
            className={`${styles.toggleButton} ${timeRange === '7d' ? styles.active : ''}`}
            onClick={() => setTimeRange('7d')}
          >
            7 Days
          </button>
          <button 
            className={`${styles.toggleButton} ${timeRange === '30d' ? styles.active : ''}`}
            onClick={() => setTimeRange('30d')}
          >
            30 Days
          </button>
          <button 
            className={`${styles.toggleButton} ${timeRange === 'all' ? styles.active : ''}`}
            onClick={() => setTimeRange('all')}
          >
            All Time
          </button>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* Health Score Card */}
      <div className={styles.healthCard}>
        <div className={styles.healthScore} style={{ borderColor: getHealthColor(analytics.healthScore) }}>
          <span className={styles.scoreValue} style={{ color: getHealthColor(analytics.healthScore) }}>
            {Math.round(analytics.healthScore)}
          </span>
          <span className={styles.scoreLabel}>Health Score</span>
        </div>
        <div className={styles.healthDetails}>
          <h3 className={styles.healthStatus} style={{ color: getHealthColor(analytics.healthScore) }}>
            {getHealthLabel(analytics.healthScore)}
          </h3>
          <div className={styles.healthMetrics}>
            <div className={styles.metric}>
              <span className={styles.metricValue}>{analytics.totalEvents.toLocaleString()}</span>
              <span className={styles.metricLabel}>Total Events</span>
            </div>
            <div className={styles.metric}>
              <span className={styles.metricValue}>{analytics.uniqueSessions.toLocaleString()}</span>
              <span className={styles.metricLabel}>Sessions</span>
            </div>
            <div className={styles.metric}>
              <span className={styles.metricValue}>{analytics.uniqueDevices}</span>
              <span className={styles.metricLabel}>Devices</span>
            </div>
            <div className={styles.metric}>
              <span className={styles.metricValue}>{analytics.uniqueUsers}</span>
              <span className={styles.metricLabel}>Users</span>
            </div>
          </div>
        </div>
      </div>

      {/* Firmware Updates Section */}
      {firmwareVersions.length > 0 && (
        <div className={styles.firmwareUpdatesCard}>
          <h2 className={styles.chartTitle}>Firmware Updates</h2>
          <div className={styles.firmwareUpdatesGrid}>
            {firmwareVersions
              .sort((a, b) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime())
              .slice(0, 4)
              .map((fw) => {
                return (
                  <div 
                    key={fw.id} 
                    className={`${styles.firmwareUpdateCard} ${fw.is_active ? styles.firmwareActive : ''}`}
                  >
                    <div className={styles.firmwareUpdateHeader}>
                      <div className={styles.firmwareVersionInfo}>
                        <span className={styles.firmwareVersionNumber}>v{fw.firmware_version}</span>
                        {fw.pico_version && (
                          <span className={styles.firmwarePicoVersion}>Pico {fw.pico_version}</span>
                        )}
                      </div>
                      {fw.is_active && (
                        <span className={styles.firmwareActiveBadge}>Active</span>
                      )}
                      {fw.is_mandatory && (
                        <span className={styles.firmwareMandatoryBadge}>Mandatory</span>
                      )}
                    </div>
                    <div className={styles.firmwareUpdateStats}>
                      <div className={styles.firmwareUpdateStat}>
                        <span className={styles.firmwareUpdateIcon}>📥</span>
                        <span className={styles.firmwareUpdateValue}>{fw.web_download_count.toLocaleString()}</span>
                        <span className={styles.firmwareUpdateLabel}>Downloads</span>
                      </div>
                      <div className={styles.firmwareUpdateStat}>
                        <span className={styles.firmwareUpdateIcon}>📱</span>
                        <span className={styles.firmwareUpdateValue}>{fw.device_update_count.toLocaleString()}</span>
                        <span className={styles.firmwareUpdateLabel}>Devices</span>
                      </div>
                    </div>
                    <div className={styles.firmwareUpdateMeta}>
                      <span className={styles.firmwareUpdateDate}>
                        Released {formatRelativeTime(fw.uploaded_at)}
              </span>
            </div>
          </div>
                );
              })}
        </div>
          {firmwareVersions.length > 4 && (
            <div className={styles.firmwareUpdateMore}>
              +{firmwareVersions.length - 4} older versions
      </div>
          )}
        </div>
      )}

      {/* Firmware Version Distribution Chart */}
      {firmwareTimeline.versions.length > 0 && (
      <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h2 className={styles.chartTitle}>Firmware Version Distribution (Handshakes)</h2>
            <div className={styles.chartToggle}>
              <button 
                className={`${styles.chartToggleBtn} ${firmwareChartRange === '24h' ? styles.active : ''}`}
                onClick={() => setFirmwareChartRange('24h')}
              >
                24h
              </button>
              <button 
                className={`${styles.chartToggleBtn} ${firmwareChartRange === '7d' ? styles.active : ''}`}
                onClick={() => setFirmwareChartRange('7d')}
              >
                7d
              </button>
              <button 
                className={`${styles.chartToggleBtn} ${firmwareChartRange === '30d' ? styles.active : ''}`}
                onClick={() => setFirmwareChartRange('30d')}
              >
                30d
              </button>
            </div>
          </div>
          
          {/* Legend */}
          <div className={styles.firmwareLegend}>
            {firmwareTimeline.versions.slice(0, 6).map((version, idx) => (
              <div key={version} className={styles.legendItem}>
                <span 
                  className={styles.legendColor} 
                  style={{ backgroundColor: getFirmwareColor(idx) }}
                />
                <span className={styles.legendLabel}>{version}</span>
              </div>
            ))}
            {firmwareTimeline.versions.length > 6 && (
              <span className={styles.legendMore}>+{firmwareTimeline.versions.length - 6} more</span>
            )}
          </div>

          {/* Stacked Bar Chart */}
          <div className={styles.firmwareChart}>
            {firmwareTimeline.days.map((day, idx) => {
              const dayDate = new Date(day.date);
              const showLabel = idx === 0 || idx === firmwareTimeline.days.length - 1 || 
                (firmwareTimeline.days.length > 10 && idx === Math.floor(firmwareTimeline.days.length / 2));
              
              return (
                <div key={day.date} className={styles.firmwareChartBar}>
                  <div className={styles.firmwareBarStack}>
                    {day.versions.slice(0, 6).map((v, vIdx) => {
                      const heightPercent = day.total > 0 ? (v.count / day.total) * 100 : 0;
                      if (heightPercent === 0) return null;
                      return (
                        <div
                          key={v.version}
                          className={styles.firmwareBarSegment}
                          style={{ 
                            height: `${heightPercent}%`,
                            backgroundColor: getFirmwareColor(vIdx),
                          }}
                          title={`${dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: ${v.version} - ${v.count} (${heightPercent.toFixed(1)}%)`}
                        />
                      );
                    })}
                  </div>
                  {showLabel ? (
                    <span className={styles.firmwareBarLabel}>
                      {dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  ) : (
                    <span className={styles.firmwareBarLabel}>&nbsp;</span>
                  )}
                  <span className={styles.firmwareBarTotal}>{day.total}</span>
                </div>
              );
            })}
          </div>
          
          {/* Summary */}
          <div className={styles.firmwareChartSummary}>
            <span>{(firmwareTimeline.totalHandshakes || 0).toLocaleString()} handshakes across {firmwareTimeline.versions.length} firmware versions</span>
          </div>
        </div>
      )}

      {/* Corruption Rates by Event Type */}
      <div className={styles.corruptionRatesCard}>
        <h2 className={styles.chartTitle}>Corruption Rates by Event Type</h2>
        <div className={styles.corruptionRatesGrid}>
          <div className={`${styles.rateCard} ${analytics.uploadCorruptionRate > 0 ? styles.rateWarning : ''}`}>
            <div className={styles.rateIcon}>📤</div>
            <div className={styles.rateInfo}>
              <span className={styles.rateValue}>
                {(analytics.uploadCorruptionRate * 100).toFixed(2)}%
              </span>
              <span className={styles.rateLabel}>Upload Corruption</span>
              <span className={styles.rateDetail}>
                {analytics.corruptionByType.file_upload.corrupted} of {analytics.corruptionByType.file_upload.total.toLocaleString()}
              </span>
            </div>
          </div>
          <div className={`${styles.rateCard} ${analytics.downloadCorruptionRate > 0 ? styles.rateWarning : ''}`}>
            <div className={styles.rateIcon}>📥</div>
            <div className={styles.rateInfo}>
              <span className={styles.rateValue}>
                {(analytics.downloadCorruptionRate * 100).toFixed(2)}%
              </span>
              <span className={styles.rateLabel}>Download Corruption</span>
              <span className={styles.rateDetail}>
                {analytics.corruptionByType.file_download.corrupted} of {analytics.corruptionByType.file_download.total.toLocaleString()}
              </span>
            </div>
          </div>
          <div className={`${styles.rateCard} ${analytics.handshakeCorruptionRate > 0 ? styles.rateWarning : ''}`}>
            <div className={styles.rateIcon}>🤝</div>
            <div className={styles.rateInfo}>
              <span className={styles.rateValue}>
                {(analytics.handshakeCorruptionRate * 100).toFixed(2)}%
              </span>
              <span className={styles.rateLabel}>Handshake Corruption</span>
              <span className={styles.rateDetail}>
                {analytics.corruptionByType.sync_handshake.corrupted} of {analytics.corruptionByType.sync_handshake.total.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Corruption Rate Trend Chart */}
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <h2 className={styles.chartTitle}>Corruption Rate Trend</h2>
          <div className={styles.chartToggle}>
            <button 
              className={`${styles.chartToggleBtn} ${corruptionTrendRange === '24h' ? styles.active : ''}`}
              onClick={() => setCorruptionTrendRange('24h')}
            >
              24h
            </button>
            <button 
              className={`${styles.chartToggleBtn} ${corruptionTrendRange === '7d' ? styles.active : ''}`}
              onClick={() => setCorruptionTrendRange('7d')}
            >
              7d
            </button>
            <button 
              className={`${styles.chartToggleBtn} ${corruptionTrendRange === '30d' ? styles.active : ''}`}
              onClick={() => setCorruptionTrendRange('30d')}
            >
              30d
            </button>
          </div>
        </div>
        <div className={styles.trendLegend}>
          <span className={styles.legendItem}>
            <span className={styles.legendDot} style={{ background: '#f97316' }} />
            Uploads
          </span>
          <span className={styles.legendItem}>
            <span className={styles.legendDot} style={{ background: '#3b82f6' }} />
            Downloads
          </span>
          <span className={styles.legendItem}>
            <span className={styles.legendDot} style={{ background: '#8b5cf6' }} />
            Handshakes
          </span>
        </div>
        <div className={styles.trendChart}>
          {corruptionTrendData.length === 0 ? (
            <div className={styles.noTrendData}>No data for selected period</div>
          ) : (
            <>
              <div className={styles.trendYAxis}>
                <span>10%</span>
                <span>5%</span>
                <span>0%</span>
              </div>
              <div className={styles.trendBars}>
                {corruptionTrendData.map((point, idx) => {
                  return (
                    <div key={idx} className={styles.trendBarGroup}>
                      <div className={styles.trendBarStack}>
                        <div 
                          className={styles.trendBar}
                          style={{ 
                            height: `${Math.min(point.uploadRate * 10, 100)}%`,
                            background: '#f97316'
                          }}
                          title={`Uploads: ${point.uploadRate.toFixed(2)}% (${point.uploadTotal} events)`}
                        />
                        <div 
                          className={styles.trendBar}
                          style={{ 
                            height: `${Math.min(point.downloadRate * 10, 100)}%`,
                            background: '#3b82f6'
                          }}
                          title={`Downloads: ${point.downloadRate.toFixed(2)}% (${point.downloadTotal} events)`}
                        />
                        <div 
                          className={styles.trendBar}
                          style={{ 
                            height: `${Math.min(point.handshakeRate * 10, 100)}%`,
                            background: '#8b5cf6'
                          }}
                          title={`Handshakes: ${point.handshakeRate.toFixed(2)}% (${point.handshakeTotal} events)`}
                        />
                      </div>
                      {(idx === 0 || idx === corruptionTrendData.length - 1 || 
                        idx === Math.floor(corruptionTrendData.length / 2)) && (
                        <span className={styles.trendLabel}>{point.label}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Deduplication & Conflicts Charts */}
      <div className={styles.chartsGrid}>
        {/* Deduplication Over Time */}
        <div className={styles.chartCard}>
          <h2 className={styles.chartTitle}>🔀 Deduplication Activity</h2>
          <div className={styles.deduplicationSummary}>
            <div className={styles.summaryItem}>
              <span className={styles.summaryValue}>{analytics.totalDeduplicationEvents.toLocaleString()}</span>
              <span className={styles.summaryLabel}>Events with Deduplication</span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryValue}>{analytics.totalDeduplicatedFiles.toLocaleString()}</span>
              <span className={styles.summaryLabel}>Files Renamed</span>
            </div>
            <div className={`${styles.summaryItem} ${analytics.totalMassDeduplication > 0 ? styles.summaryAlert : ''}`}>
              <span className={styles.summaryValue}>{analytics.totalMassDeduplication.toLocaleString()}</span>
              <span className={styles.summaryLabel}>🚨 Mass Events</span>
            </div>
          </div>
          {analytics.dailyStats.some(d => d.deduplicationEvents > 0) ? (
            <div className={styles.miniChart}>
              {(() => {
                // Sort chronologically: oldest first (left) to newest (right)
                const sortedDays = [...analytics.dailyStats].sort(
                  (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
                );
                const maxFiles = Math.max(...sortedDays.map(d => d.deduplicatedFiles), 1);
                
                return sortedDays.map((day, idx) => {
                  const heightPercent = (day.deduplicatedFiles / maxFiles) * 100;
                  const showLabel = idx === 0 || idx === sortedDays.length - 1 || 
                    (sortedDays.length > 14 && idx === Math.floor(sortedDays.length / 2));
                  
                  return (
                    <div key={day.date} className={styles.miniChartBar}>
                      <div 
                        className={`${styles.miniBar} ${day.massDeduplication > 0 ? styles.miniBarAlert : styles.miniBarNormal}`}
                        style={{ height: `${day.deduplicatedFiles > 0 ? Math.max(heightPercent, 4) : 0}%` }}
                        title={`${new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: ${day.deduplicatedFiles} files renamed (${day.deduplicationEvents} events${day.massDeduplication > 0 ? `, ${day.massDeduplication} mass` : ''})`}
                      />
                      {showLabel && (
                        <span className={styles.miniChartLabel}>
                          {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            <div className={styles.noDataMessage}>
              <span className={styles.noDataIcon}>✅</span>
              <span>No deduplication events in this period</span>
            </div>
          )}
        </div>

        {/* Conflicts Over Time */}
        <div className={styles.chartCard}>
          <h2 className={styles.chartTitle}>⚡ File Conflicts</h2>
          <div className={styles.conflictsSummary}>
            <div className={styles.summaryItem}>
              <span className={styles.summaryValue}>{analytics.totalConflictsCreated.toLocaleString()}</span>
              <span className={styles.summaryLabel}>Conflicts Created</span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryValue}>{analytics.totalConflictsResolved.toLocaleString()}</span>
              <span className={styles.summaryLabel}>Conflicts Resolved</span>
            </div>
            <div className={`${styles.summaryItem} ${(analytics.totalConflictsCreated - analytics.totalConflictsResolved) > 0 ? styles.summaryWarning : styles.summaryGood}`}>
              <span className={styles.summaryValue}>
                {analytics.totalConflictsCreated - analytics.totalConflictsResolved}
              </span>
              <span className={styles.summaryLabel}>Pending</span>
            </div>
          </div>
          {analytics.dailyStats.some(d => d.conflictsCreated > 0 || d.conflictsResolved > 0) ? (
            <div className={styles.miniChart}>
              {(() => {
                // Sort chronologically: oldest first (left) to newest (right)
                const sortedDays = [...analytics.dailyStats].sort(
                  (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
                );
                const maxConflicts = Math.max(...sortedDays.map(d => Math.max(d.conflictsCreated, d.conflictsResolved)), 1);
                
                return sortedDays.map((day, idx) => {
                  const createdHeight = (day.conflictsCreated / maxConflicts) * 100;
                  const resolvedHeight = (day.conflictsResolved / maxConflicts) * 100;
                  const showLabel = idx === 0 || idx === sortedDays.length - 1 || 
                    (sortedDays.length > 14 && idx === Math.floor(sortedDays.length / 2));
                  
                  return (
                    <div key={day.date} className={styles.miniChartBar}>
                      <div className={styles.stackedBar}>
                        <div 
                          className={styles.stackedBarCreated}
                          style={{ height: `${day.conflictsCreated > 0 ? Math.max(createdHeight, 4) : 0}%` }}
                          title={`${new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: ${day.conflictsCreated} created`}
                        />
                        <div 
                          className={styles.stackedBarResolved}
                          style={{ height: `${day.conflictsResolved > 0 ? Math.max(resolvedHeight, 4) : 0}%` }}
                          title={`${new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: ${day.conflictsResolved} resolved`}
                        />
                      </div>
                      {showLabel && (
                        <span className={styles.miniChartLabel}>
                          {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            <div className={styles.noDataMessage}>
              <span className={styles.noDataIcon}>✅</span>
              <span>No conflicts in this period</span>
            </div>
          )}
          {analytics.dailyStats.some(d => d.conflictsCreated > 0 || d.conflictsResolved > 0) && (
            <div className={styles.chartLegendRow}>
              <span className={styles.legendItemInline}>
                <span className={styles.legendDotCreated} /> Created
              </span>
              <span className={styles.legendItemInline}>
                <span className={styles.legendDotResolved} /> Resolved
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Key Stats Row */}
      <div className={styles.statsRow}>
        <div className={styles.statBox}>
          <div className={styles.statIcon}>📊</div>
          <div className={styles.statInfo}>
            <span className={styles.statNumber}>{analytics.eventsLast24h.toLocaleString()}</span>
            <span className={styles.statDesc}>Events (24h)</span>
          </div>
        </div>
        <div className={`${styles.statBox} ${analytics.totalCorrupted > 0 ? styles.warningBox : ''}`}>
          <div className={styles.statIcon}>⚠️</div>
          <div className={styles.statInfo}>
            <span className={styles.statNumber}>{analytics.totalCorrupted.toLocaleString()}</span>
            <span className={styles.statDesc}>Corrupted</span>
          </div>
        </div>
        <div className={`${styles.statBox} ${analytics.totalQuarantined > 0 ? styles.alertBox : ''}`}>
          <div className={styles.statIcon}>🔒</div>
          <div className={styles.statInfo}>
            <span className={styles.statNumber}>{analytics.totalQuarantined.toLocaleString()}</span>
            <span className={styles.statDesc}>Quarantined</span>
          </div>
        </div>
        <div className={`${styles.statBox} ${analytics.totalErrors > 0 ? styles.errorBox : ''}`}>
          <div className={styles.statIcon}>❌</div>
          <div className={styles.statInfo}>
            <span className={styles.statNumber}>{analytics.totalErrors.toLocaleString()}</span>
            <span className={styles.statDesc}>Errors</span>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className={styles.chartsGrid}>
      {/* Daily Trend Chart */}
      <div className={styles.chartCard}>
        <h2 className={styles.chartTitle}>Daily Event Volume</h2>
        <div className={styles.chart}>
          <div className={styles.chartBars}>
              {analytics.dailyStats.map((day) => (
              <div key={day.date} className={styles.barGroup}>
                <div 
                  className={styles.bar}
                  style={{ height: `${(day.total / maxDailyTotal) * 100}%` }}
                  title={`${day.date}: ${day.total} events`}
                  >
                    {day.corrupted > 0 && (
                      <div 
                        className={styles.barCorruptedOverlay}
                        style={{ height: `${(day.corrupted / day.total) * 100}%` }}
                        title={`${day.corrupted} corrupted`}
                      />
                    )}
                  </div>
              </div>
            ))}
          </div>
            <div className={styles.chartLabels}>
              {analytics.dailyStats.filter((_, i, arr) => 
                i === 0 || i === arr.length - 1 || i === Math.floor(arr.length / 2)
              ).map(day => (
                <span key={day.date} className={styles.chartLabel}>{day.date.slice(5)}</span>
              ))}
        </div>
      </div>
          <div className={styles.chartLegend}>
          <span className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: '#3b82f6' }} />
              Total Events
          </span>
          <span className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: '#ef4444' }} />
              Corrupted
          </span>
        </div>
        </div>

        {/* Hourly Distribution */}
        <div className={styles.chartCard}>
          <h2 className={styles.chartTitle}>Activity by Hour (UTC)</h2>
        <div className={styles.chart}>
          <div className={styles.chartBars}>
              {analytics.hourlyDistribution.map((count, hour) => (
                <div key={hour} className={styles.barGroup}>
                  <div 
                    className={styles.barHour}
                    style={{ height: `${(count / maxHourly) * 100}%` }}
                    title={`${hour}:00 - ${count} events`}
                  />
                </div>
              ))}
            </div>
            <div className={styles.chartLabels}>
              <span className={styles.chartLabel}>0h</span>
              <span className={styles.chartLabel}>6h</span>
              <span className={styles.chartLabel}>12h</span>
              <span className={styles.chartLabel}>18h</span>
              <span className={styles.chartLabel}>24h</span>
              </div>
          </div>
        </div>
      </div>

      {/* Event Type Distribution */}
      <div className={styles.chartCard}>
        <h2 className={styles.chartTitle}>Event Type Distribution</h2>
        <div className={styles.typeDistribution}>
          {Object.entries(analytics.eventsByType)
            .sort(([, a], [, b]) => b - a)
            .map(([type, count]) => {
              const percentage = (count / analytics.totalEvents) * 100;
              return (
                <div key={type} className={styles.typeRow}>
                  <div className={styles.typeInfo}>
                    <span className={styles.typeIcon}>{getEventTypeIcon(type as EventType)}</span>
                    <span className={styles.typeName}>{getEventTypeLabel(type as EventType)}</span>
                  </div>
                  <div className={styles.typeBar}>
                    <div 
                      className={styles.typeBarFill}
                      style={{ width: `${percentage}%` }}
                  />
                </div>
                  <div className={styles.typeStats}>
                    <span className={styles.typeCount}>{count.toLocaleString()}</span>
                    <span className={styles.typePercent}>{percentage.toFixed(1)}%</span>
              </div>
          </div>
              );
            })}
        </div>
      </div>

      {/* Breakdown Cards Row */}
      <div className={styles.breakdownRow}>
        {/* Corruption Types */}
        <div className={styles.breakdownCard}>
          <h2 className={styles.chartTitle}>Corruption Types</h2>
          {Object.keys(analytics.corruptionCategories).length === 0 ? (
            <p className={styles.noData}>No corruption detected 🎉</p>
          ) : (
            <div className={styles.breakdownList}>
              {Object.entries(analytics.corruptionCategories)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 5)
                .map(([category, count]) => {
                  const percentage = (count / analytics.totalCorrupted) * 100;
                  return (
                    <div key={category} className={styles.breakdownItem}>
                  <div className={styles.breakdownHeader}>
                        <span className={styles.breakdownCategory}>{category.replace(/_/g, ' ')}</span>
                        <span className={styles.breakdownCount}>{count}</span>
                  </div>
                  <div className={styles.breakdownBar}>
                    <div 
                      className={styles.breakdownFill}
                          style={{ width: `${percentage}%`, background: '#fbbf24' }}
                    />
                  </div>
                </div>
                  );
                })}
            </div>
          )}
        </div>

        {/* Quarantine Reasons */}
        <div className={styles.breakdownCard}>
          <h2 className={styles.chartTitle}>Quarantine Reasons</h2>
          {Object.keys(analytics.quarantineReasons).length === 0 ? (
            <p className={styles.noData}>No quarantined files 🎉</p>
          ) : (
            <div className={styles.breakdownList}>
              {Object.entries(analytics.quarantineReasons)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 5)
                .map(([reason, count]) => {
                  const percentage = (count / analytics.totalQuarantined) * 100;
                  const label = QUARANTINE_REASON_LABELS[reason as keyof typeof QUARANTINE_REASON_LABELS] || reason;
                  const barColor = reason === 'SEVERE_TRUNCATION' ? '#fb923c' : 
                                  reason === 'EMPTY_UPLOAD' ? '#9ca3af' : '#a78bfa';
                  return (
                    <div key={reason} className={styles.breakdownItem}>
                  <div className={styles.breakdownHeader}>
                        <span className={styles.breakdownCategory}>{label}</span>
                        <span className={styles.breakdownCount}>{count}</span>
                  </div>
                  <div className={styles.breakdownBar}>
                    <div 
                      className={styles.breakdownFill}
                          style={{ width: `${percentage}%`, background: barColor }}
                    />
                  </div>
                </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      {/* Firmware Analysis */}
      {Object.keys(analytics.firmwareVersions).length > 0 && (
        <div className={styles.tableCard}>
          <h2 className={styles.chartTitle}>Firmware Version Analysis</h2>
          <div className={styles.firmwareGrid}>
            {Object.entries(analytics.firmwareVersions)
              .sort(([, a], [, b]) => b.total - a.total)
              .slice(0, 8)
              .map(([version, data]) => {
                const uploadRate = data.uploads.total > 0 ? (data.uploads.corrupted / data.uploads.total) * 100 : 0;
                const downloadRate = data.downloads.total > 0 ? (data.downloads.corrupted / data.downloads.total) * 100 : 0;
                const handshakeRate = data.handshakes.total > 0 ? (data.handshakes.corrupted / data.handshakes.total) * 100 : 0;
                const hasIssues = uploadRate > 0 || downloadRate > 0 || handshakeRate > 0;
              return (
                  <div key={version} className={`${styles.firmwareCard} ${hasIssues ? styles.firmwareCardWarning : ''}`}>
                    <div className={styles.firmwareHeader}>
                      <span className={styles.firmwareVersion}>{version}</span>
                      <span className={styles.firmwareTotal}>{data.total.toLocaleString()} events</span>
                    </div>
                    <div className={styles.firmwareRates}>
                      <div className={styles.firmwareRate}>
                        <span className={styles.firmwareRateIcon}>📤</span>
                        <span className={uploadRate > 0 ? styles.firmwareWarning : styles.firmwareGood}>
                          {uploadRate.toFixed(1)}%
                        </span>
                      </div>
                      <div className={styles.firmwareRate}>
                        <span className={styles.firmwareRateIcon}>📥</span>
                        <span className={downloadRate > 0 ? styles.firmwareWarning : styles.firmwareGood}>
                          {downloadRate.toFixed(1)}%
                        </span>
                      </div>
                      <div className={styles.firmwareRate}>
                        <span className={styles.firmwareRateIcon}>🤝</span>
                        <span className={handshakeRate > 0 ? styles.firmwareWarning : styles.firmwareGood}>
                          {handshakeRate.toFixed(1)}%
                        </span>
                      </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
}
